// src/components/admin/StaffManagement.js
import React, { useState, useEffect } from 'react';
import apiService from '../../services/ApiServices';
import {
  Container, Row, Col, Card, Table, Badge, Button,
  Form, InputGroup, Spinner, Pagination, Alert, Modal
} from 'react-bootstrap';
import {
  FaUserTie, FaPlus, FaSearch, FaCheckCircle, FaTimesCircle,
  FaEnvelope, FaUserShield, FaClock, FaUniversity, FaArrowLeft, FaEdit, FaTrash
} from 'react-icons/fa';

const StaffManagement = () => {
  const [staff, setStaff] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [collegeSearch, setCollegeSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [collegeId, setCollegeId] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [roles, setRoles] = useState([]);
  const [colleges, setColleges] = useState([]);

  // Change Role Modal states
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedStaffForRole, setSelectedStaffForRole] = useState(null);
  const [newRoleId, setNewRoleId] = useState('');

  // Delete Confirmation Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState(null);

  // Create staff modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    userName: '',
    email: '',
    password: '',
    role_id: '',
    otp: '',
    college_id: ''
  });
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);

  // Pagination & Search
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 10;

  // Get logged-in user info
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user?.college_id) {
      setCollegeId(user.college_id);
      setIsSuperAdmin(false);
      setSelectedCollege({ college_id: user.college_id, college_name: 'My College' });
    } else {
      setIsSuperAdmin(true);
      setCollegeId(null);
      fetchColleges();
    }
  }, []);

  // Fetch staff list
  const fetchStaff = async () => {
    if (!selectedCollege) return;
    try {
      setLoading(true);
      setError(null);

      let url;

      // Case 1: Specific college selected
      if (selectedCollege.college_id) {
        url = `/api/user-admin/non-students?collegeId=${selectedCollege.college_id}`;
      }
      // Case 2: Super admin (no college_id)
      else {
        url = '/api/user-admin/non-students';
      }

      const response = await apiService.get(url);

      if (response.success && Array.isArray(response.message?.users)) {
        let fetchedStaff = response.message.users;
        if (selectedCollege.college_id === null) {
          fetchedStaff = fetchedStaff.filter(user => user.college_id === null);
        }
        setStaff(fetchedStaff);
      } else {
        setError('Failed to load staff list');
      }
    } catch (err) {
      setError('Error loading staff: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Fetch roles for dropdown
  const fetchRoles = async () => {
    try {
      let url;
      if (selectedCollege?.college_id) {
        url = `/api/user-admin/staff/${selectedCollege.college_id}`;
      } else {
        url = '/api/roles';
      }

      const response = await apiService.get(url);

      if (response.success && Array.isArray(response.message?.roles)) {
        let fetchedRoles = response.message.roles;
        if (selectedCollege?.college_id === null) {
          fetchedRoles = fetchedRoles.filter(role => role.college_id === null);
        }
        setRoles(fetchedRoles);
      }
    } catch (err) {
      console.error('Error loading roles:', err);
    }
  };

  // Fetch colleges (for Super Admin)
  const fetchColleges = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/api/college');
      if (response.success && Array.isArray(response.message?.showCollege)) {
        setColleges(response.message.showCollege);
      }
    } catch (err) {
      console.error('Error loading colleges:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch when we know whether it's college or super admin
    if (selectedCollege) {
      fetchStaff();
      fetchRoles();
    }
  }, [selectedCollege]);

  // Search filter (client-side)
  const filteredStaff = staff.filter(member =>
    member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.user_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredColleges = colleges.filter(college =>
    college.college_name?.toLowerCase().includes(collegeSearch.toLowerCase()) ||
    college.college_code?.toLowerCase().includes(collegeSearch.toLowerCase())
  );

  const totalPages = Math.ceil(filteredStaff.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredStaff.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Send OTP
  const handleSendOtp = async () => {
    if (!formData.email.trim()) {
      setError('Email is required to send OTP');
      return;
    }

    try {
      setSendingOtp(true);
      setError(null);

      const response = await apiService.post('/api/otp/send', {
        email: formData.email.trim()
      });

      if (response.success) {
        setOtpSent(true);
        setSuccessMsg('OTP sent successfully to your email!');
      } else {
        setError(response.message || 'Failed to send OTP');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error sending OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  // Create Staff
  const handleCreateStaff = async () => {
    if (!formData.name.trim() || !formData.userName.trim() || !formData.email.trim() ||
        !formData.password.trim() || !formData.otp.trim() || !formData.role_id) {
      setError('All fields are required');
      return;
    }

    try {
      setError(null);

      const payload = {
        name: formData.name.trim(),
        userName: formData.userName.trim(),
        email: formData.email.trim(),
        password: formData.password.trim(),
        otp: formData.otp.trim(),
        isActive: true,
        role_id: Number(formData.role_id),
      };

      if (selectedCollege?.college_id) {
        payload.college_id = selectedCollege.college_id;
      }

      const response = await apiService.post('/api/user-admin', payload);

      if (response.success) {
        setSuccessMsg('Staff member created successfully!');
        setShowCreateModal(false);
        fetchStaff();
        // Reset form
        setFormData({
          name: '', userName: '', email: '', password: '', otp: '', role_id: '', college_id: ''
        });
        setOtpSent(false);
      } else {
        setError(response.message || 'Failed to create staff');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error creating staff');
    }
  };

  // Open Change Role Modal
  const openRoleModal = (staffMember) => {
    setSelectedStaffForRole(staffMember);
    setNewRoleId(staffMember.role_id || '');
    setShowRoleModal(true);
  };

  // Handle Change Role
  const handleChangeRole = async () => {
    if (!selectedStaffForRole || !newRoleId) return;

    try {
      setActionLoading(selectedStaffForRole.user_id);
      const response = await apiService.patch(`/api/user-admin/users/${selectedStaffForRole.user_id}/role`, {
        role_id: Number(newRoleId)
      });

      if (response.success) {
        setSuccessMsg('Role updated successfully');
        setShowRoleModal(false);
        fetchStaff(); // Refresh list
      } else {
        setError(response.message || 'Failed to update role');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating role');
    } finally {
      setActionLoading(null);
      setSelectedStaffForRole(null);
      setNewRoleId('');
    }
  };

  // Handle Delete Staff (Set role to 0)
  const handleDeleteStaff = (staffMember) => {
    setStaffToDelete(staffMember);
    setShowDeleteModal(true);
  };

  const confirmDeleteStaff = async () => {
    if (!staffToDelete) return;

    try {
      setActionLoading(staffToDelete.user_id);
      const response = await apiService.patch(`/api/user-admin/users/${staffToDelete.user_id}/role`, {
        role_id: null
      });

      if (response.success) {
        setSuccessMsg('Staff member removed successfully');
        setShowDeleteModal(false);
        fetchStaff(); // Refresh list
      } else {
        setError(response.message || 'Failed to remove staff');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error removing staff');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h2 className="mb-0">
            <FaUserTie className="me-2 text-primary" />
            Staff Management
          </h2>
        </Col>
      </Row>

      {/* College Selection View */}
      {isSuperAdmin && !selectedCollege && (
        <>
          <InputGroup className="mb-4">
            <InputGroup.Text><FaSearch /></InputGroup.Text>
            <Form.Control
              placeholder="Search college by name or code..."
              value={collegeSearch}
              onChange={(e) => setCollegeSearch(e.target.value)}
            />
          </InputGroup>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <Row>
              {/* Non-College Tile */}
              <Col md={4} lg={3} className="mb-4">
                <Card
                  className="shadow-sm h-100 text-center cursor-pointer"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedCollege({ college_id: null, college_name: 'System Staff' })}
                >
                  <Card.Body className="d-flex flex-column justify-content-center">
                    <FaUserShield size={50} className="text-primary mb-3 mx-auto" />
                    <h5>System Staff</h5>
                    <p className="text-muted small">Global / System-level staff</p>
                  </Card.Body>
                </Card>
              </Col>

              {/* College Tiles */}
              {filteredColleges.map(college => (
                <Col md={4} lg={3} key={college.college_id} className="mb-4">
                  <Card
                    className="shadow-sm h-100 text-center cursor-pointer"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedCollege(college)}
                  >
                    <Card.Body className="d-flex flex-column justify-content-center">
                      <FaUniversity size={50} className="text-primary mb-3 mx-auto" />
                      <h5>{college.college_name}</h5>
                      {college.college_code && (
                        <Badge bg="info" className="mx-auto">{college.college_code}</Badge>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </>
      )}

      {selectedCollege && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              {isSuperAdmin && (
                <Button
                  variant="outline-secondary"
                  className="mb-2"
                  onClick={() => {
                    setSelectedCollege(null);
                    setStaff([]);
                    setCollegeSearch('');
                    setSearchTerm('');
                  }}
                >
                  <FaArrowLeft className="me-2" /> Back to College List
                </Button>
              )}
              <h4 className="mb-0">
                Staff for <strong>{selectedCollege.college_name}</strong>
              </h4>
            </div>
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              <FaPlus className="me-2" /> Add New Staff
            </Button>
          </div>

      {/* Messages */}
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert variant="success" dismissible onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

      {/* Search */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text><FaSearch /></InputGroup.Text>
                <Form.Control
                  placeholder="Search by name, email or username..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </InputGroup>
            </Col>
            <Col md={6} className="text-md-end">
              <Badge bg="info" className="fs-6 px-3 py-2">
                {filteredStaff.length} staff members
              </Badge>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Main Content */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading staff members...</p>
        </div>
      ) : currentItems.length === 0 ? (
        <Card className="text-center py-5 border-0 shadow-sm">
          <Card.Body>
            <FaUserTie size={60} className="text-muted mb-3" />
            <h5 className="text-muted">No staff found</h5>
            <p className="text-muted">
              {searchTerm ? 'Try different search term' : 'No staff members yet'}
            </p>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Username</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((member, index) => (
                    <tr key={member.user_id}>
                      <td>{indexOfFirstItem + index + 1}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div
                            className="bg-dark text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                            style={{ width: '40px', height: '40px', fontSize: '1.1rem' }}
                          >
                            {member.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <strong>{member.name}</strong>
                            <div className="text-muted small">{member.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>{member.email}</td>
                      <td>{member.user_name}</td>
                      <td>
                        <Badge bg="secondary">
                          {member.role?.role_name || 'Staff'}
                        </Badge>
                      </td>
                      <td>
                        {member.account_status?.toLowerCase() === 'approved' ? (
                          <Badge bg="success">Active</Badge>
                        ) : (
                          <Badge bg="warning">{member.account_status}</Badge>
                        )}
                      </td>
                      <td>
                        <small className="text-muted">
                          {new Date(member.created_at).toLocaleDateString('en-IN', {
                            day: 'numeric', month: 'short', year: 'numeric'
                          })}
                        </small>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="d-inline-flex align-items-center"
                            onClick={() => openRoleModal(member)}
                          >
                            <FaUserShield className="me-2" /> Change Role
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="d-inline-flex align-items-center"
                            onClick={() => handleDeleteStaff(member)}
                            disabled={actionLoading === member.user_id}
                          >
                            {actionLoading === member.user_id ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <><FaTrash className="me-2" /> Delete</>
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <Pagination size="sm">
                <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  if (page === 1 || page === totalPages || Math.abs(page - currentPage) <= 2) {
                    return (
                      <Pagination.Item
                        key={page}
                        active={page === currentPage}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </Pagination.Item>
                    );
                  }
                  return null;
                })}
                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
              </Pagination>
            </div>
          )}
        </>
      )}
      </>
      )}

      {/* Create Staff Modal */}
      <Modal show={showCreateModal} onHide={() => {
        setShowCreateModal(false);
        setOtpSent(false);
        setFormData({ name: '', userName: '', email: '', password: '', otp: '', role_id: '', college_id: '' });
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New Staff Member</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {selectedCollege && (
              <Alert variant="info" className="py-2">
                Adding staff to: <strong>{selectedCollege.college_name}</strong>
              </Alert>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name *</Form.Label>
                  <Form.Control
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. John Doe"
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Username *</Form.Label>
                  <Form.Control
                    value={formData.userName}
                    onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
                    placeholder="e.g. johnstaff123"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="staff@example.com"
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Password *</Form.Label>
                  <Form.Control
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="********"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role *</Form.Label>
                  <Form.Select
                    value={formData.role_id}
                    onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                    required
                  >
                    <option value="">Select Role</option>
                    {roles.map((role) => (
                      <option key={role.role_id} value={role.role_id}>
                        {role.role_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* OTP Section */}
            {!otpSent ? (
              <div className="mt-4">
                <Button
                  variant="outline-primary"
                  onClick={handleSendOtp}
                  disabled={sendingOtp || !formData.email.trim()}
                >
                  {sendingOtp ? (
                    <>
                      <Spinner animation="border" size="sm" /> Sending OTP...
                    </>
                  ) : (
                    'Send OTP to Email'
                  )}
                </Button>
              </div>
            ) : (
              <Form.Group className="mt-4">
                <Form.Label>Enter OTP sent to {formData.email}</Form.Label>
                <Form.Control
                  value={formData.otp}
                  onChange={(e) => setFormData({ ...formData, otp: e.target.value })}
                  placeholder="Enter 6-digit OTP"
                  maxLength={6}
                  required
                />
              </Form.Group>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          {otpSent && (
            <Button variant="primary" onClick={handleCreateStaff}>
              Create Staff
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Change Role Modal */}
      <Modal show={showRoleModal} onHide={() => setShowRoleModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Change Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedStaffForRole && (
            <>
              <p>Changing role for <strong>{selectedStaffForRole.name}</strong></p>
              <Form.Group>
                <Form.Label>Select New Role</Form.Label>
                <Form.Select
                  value={newRoleId}
                  onChange={(e) => setNewRoleId(e.target.value)}
                >
                  <option value="">Select Role</option>
                  {roles.map(role => (
                    <option key={role.role_id} value={role.role_id}>
                      {role.role_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRoleModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleChangeRole}
            disabled={actionLoading === selectedStaffForRole?.user_id || !newRoleId}
          >
            {actionLoading === selectedStaffForRole?.user_id ? <Spinner size="sm" animation="border" /> : 'Update Role'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {staffToDelete && (
            <>
              <p>Are you sure you want to remove <strong>{staffToDelete.name}</strong>?</p>
              <p className="text-muted small">This will revoke their staff access permissions.</p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={confirmDeleteStaff}
            disabled={actionLoading === staffToDelete?.user_id}
          >
            {actionLoading === staffToDelete?.user_id ? <Spinner size="sm" animation="border" /> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default StaffManagement;